import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CreditCard, DollarSign, TrendingUp, Users } from "lucide-react";
import { format } from "date-fns";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const COLORS = ["#129E8C", "#f59e0b", "#8b5cf6", "#ef4444", "#3b82f6"];

export default function AdminBilling() {
  const { data: subscriptions, isLoading } = useQuery({
    queryKey: ["admin-billing-subs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_exam_access")
        .select("*")
        .order("activated_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const { data: pricing } = useQuery({
    queryKey: ["admin-billing-pricing"],
    queryFn: async () => {
      const { data } = await supabase.from("exam_pricing").select("*").order("exam_slug");
      return data || [];
    },
  });

  const now = new Date();
  const active = subscriptions?.filter(s => s.expires_at && new Date(s.expires_at) > now) || [];
  const expired = subscriptions?.filter(s => s.expires_at && new Date(s.expires_at) <= now) || [];

  // Breakdown by type
  const typeCounts: Record<string, number> = {};
  active.forEach(s => {
    const t = s.subscription_type || "trial";
    typeCounts[t] = (typeCounts[t] || 0) + 1;
  });
  const pieData = Object.entries(typeCounts).map(([name, value]) => ({ name, value }));

  // Breakdown by exam
  const examCounts: Record<string, number> = {};
  active.forEach(s => {
    examCounts[s.exam_slug] = (examCounts[s.exam_slug] || 0) + 1;
  });

  const churnRate = subscriptions && subscriptions.length > 0
    ? Math.round((expired.length / subscriptions.length) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Revenue KPIs */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground uppercase">Active Subscribers</span>
              <Users className="h-4 w-4 text-emerald-500" />
            </div>
            <div className="text-2xl font-bold">{active.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground uppercase">Total Subscriptions</span>
              <CreditCard className="h-4 w-4 text-blue-500" />
            </div>
            <div className="text-2xl font-bold">{subscriptions?.length || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground uppercase">Churn Rate</span>
              <TrendingUp className="h-4 w-4 text-amber-500" />
            </div>
            <div className="text-2xl font-bold">{churnRate}%</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground uppercase">Pricing Plans</span>
              <DollarSign className="h-4 w-4 text-violet-500" />
            </div>
            <div className="text-2xl font-bold">{pricing?.length || 0}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Subscription by Type */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">By Subscription Type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[220px]">
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value" label={({ name, value }) => `${name} (${value})`}>
                      {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-sm text-muted-foreground">No data</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* By Exam */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">By Exam Type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(examCounts).map(([exam, count]) => (
                <div key={exam} className="flex items-center justify-between">
                  <span className="text-sm font-medium">{exam}</span>
                  <Badge variant="secondary">{count} active</Badge>
                </div>
              ))}
              {Object.keys(examCounts).length === 0 && (
                <p className="text-sm text-muted-foreground">No active subscriptions</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Subscriptions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Active Subscriptions</CardTitle>
          <CardDescription>{active.length} currently active</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User ID</TableHead>
                <TableHead>Exam</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Activated</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Auto-Renew</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={6} className="text-center">Loading...</TableCell></TableRow>
              ) : active.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No active subscriptions</TableCell></TableRow>
              ) : (
                active.slice(0, 50).map((sub) => (
                  <TableRow key={`${sub.user_id}-${sub.exam_slug}`}>
                    <TableCell className="font-mono text-xs">{sub.user_id.slice(0, 8)}...</TableCell>
                    <TableCell>{sub.exam_slug}</TableCell>
                    <TableCell><Badge variant="outline">{sub.subscription_type || "trial"}</Badge></TableCell>
                    <TableCell className="text-sm">{format(new Date(sub.activated_at), "MMM dd, yyyy")}</TableCell>
                    <TableCell className="text-sm">{sub.expires_at ? format(new Date(sub.expires_at), "MMM dd, yyyy") : "—"}</TableCell>
                    <TableCell>{sub.auto_renew ? <Badge>Yes</Badge> : <Badge variant="secondary">No</Badge>}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pricing Plans */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pricing Plans</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Exam</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Stripe Price ID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pricing?.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{p.exam_slug}</TableCell>
                  <TableCell>{p.period}</TableCell>
                  <TableCell>{p.display_price}</TableCell>
                  <TableCell className="font-mono text-xs">{p.stripe_price_id || "Dynamic"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
